import { DISABLE_LIST_QUERY_OPTIONS } from 'constants/disableListQueryOptions';
import {
  EXPORT_TYPE_NAMES,
  ExportTypes,
  ExportJobTypes,
  ExportTransportTypes,
  ReportNames,
} from 'constants/exportTypes';
import { ExportType } from 'constants/exportTypes';
import { Resources } from 'constants/resources';

import React, { useState } from 'react';

import { useApiContext } from 'context/apiContext';
import { useReportExport } from 'hooks/queries/useReportExport';
import {
  useListController,
  SortPayload,
  ListContextProvider,
  useNotify,
  ListControllerResult,
} from 'react-admin';
import { ExportTypesButton } from 'shared/mui/Buttons/ExportTypesButton/ExportTypesButton';
import { PageHeader } from 'shared/mui/PageHeader';
import { Typography } from 'shared/mui/Typography';
import { ExportEditButton } from 'shared/react-admin/Export/ExportEditButton/ExportEditButton';
import { ReportExportDialog } from 'shared/widgets/ReportExportDialog/ReportExportDialog';
import { RefundChargebackReportOrder } from 'types/embedded/refundChargebackReport';

import { EXPORT_FIELDS } from './constants/exportFields';
import { useGetRefundChargebackReport } from './hooks/useGetRefundChargebackReport';
import { RefundChargebackReportListContent } from './RefundChargebackReportListContent';

export const RefundChargebackReportList: React.FC = () => {
  const { filterValues, ...restContext } = useListController<RefundChargebackReportOrder>({
    resource: Resources.RefundChargebackReport,
    sort: {} as SortPayload,
    queryOptions: DISABLE_LIST_QUERY_OPTIONS,
  });

  const { report, total, isLoading: isReportLoading } = useGetRefundChargebackReport(filterValues);

  const [isOpenNotifyDialog, setIsOpenNotifyDialog] = useState(false);
  const [isSendReportOnEmail, setIsSendReportOnEmail] = useState(false);
  const [exportType, setExportType] = useState<ExportType>(ExportTypes.Excel);

  const { user } = useApiContext();
  const notify = useNotify();

  const { mutate: exportMutate, isLoading: isExportLoading } = useReportExport({
    filters: {
      ...filterValues,
      exportType: exportType,
      jobType: ExportJobTypes.Refund_chargeback,
      ...(isSendReportOnEmail && { notificationTransport: ExportTransportTypes.Email }),
    },
    preferenceKey: ReportNames.Refund_chargeback_report,
    onSuccess: () => {
      notify(
        `Файл экспорта отчёта по рефандам и чарджбекам формируется ${isSendReportOnEmail ? 'и будет отправлен вам на почту' : ''}`,
        {
          type: 'info',
        },
      );
      setIsOpenNotifyDialog(false);
    },
    onError: (error) => {
      notify(error.data?.errors[0]?.title, { type: 'error' });
    },
  });

  return (
    <ListContextProvider
      value={
        {
          filterValues,
          ...restContext,
          data: report?.orders || [],
          total: total,
          isLoading: isReportLoading,
        } as ListControllerResult
      }
    >
      <PageHeader
        actions={[
          <ExportTypesButton
            disabled={!total}
            exportType={exportType}
            key="export-button"
            onChangeType={setExportType}
            onClick={() => setIsOpenNotifyDialog(true)}
          />,
        ]}
        headerTitle={{
          titleText: 'Отчёт по рефандам и чарджбекам',
        }}
        mt={3}
      />
      <RefundChargebackReportListContent summary={report?.summary} />
      <ReportExportDialog
        exportType={EXPORT_TYPE_NAMES[exportType]}
        isLoading={isReportLoading || isExportLoading}
        onChange={() => setIsSendReportOnEmail((prev) => !prev)}
        onClose={() => setIsOpenNotifyDialog(false)}
        onSubmit={exportMutate}
        open={isOpenNotifyDialog}
        sendOnEmail={isSendReportOnEmail}
        title={
          <>
            <Typography component="span" sx={{ verticalAlign: 'middle' }} variant="inherit">
              Экспорт отчёта
            </Typography>
            <ExportEditButton
              fields={EXPORT_FIELDS}
              preferenceKey={ReportNames.Refund_chargeback_report}
            />
          </>
        }
        userEmail={user?.corporateEmail || user?.email}
      />
    </ListContextProvider>
  );
};
